import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, memoryLocalCache, initializeFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { devLog } from './utils/debugLogger';

// Firebase configuration - loaded from environment variables
// For local dev: set in .env file (see .env.example)
// For production: set in GitHub Secrets
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Log config status (not values) for debugging - dev only
devLog('[Firebase] Config loaded:', {
  apiKey: firebaseConfig.apiKey ? '✓ set' : '✗ missing',
  authDomain: firebaseConfig.authDomain ? '✓ set' : '✗ missing',
  projectId: firebaseConfig.projectId ? '✓ set' : '✗ missing',
  storageBucket: firebaseConfig.storageBucket ? '✓ set' : '✗ missing',
  messagingSenderId: firebaseConfig.messagingSenderId ? '✓ set' : '✗ missing',
  appId: firebaseConfig.appId ? '✓ set' : '✗ missing',
  measurementId: firebaseConfig.measurementId ? '✓ set' : '✗ missing',
});

// Initialize Firebase
console.log('[iOS-DEBUG] 🚀 Starting Firebase initialization...');
console.log('[iOS-DEBUG] Config check:', {
  hasApiKey: !!firebaseConfig.apiKey,
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasProjectId: !!firebaseConfig.projectId
});

let app;
try {
  app = initializeApp(firebaseConfig);
  console.log('[iOS-DEBUG] ✅ Firebase app initialized:', app.name);
} catch (error) {
  console.error('[iOS-DEBUG] ❌ Firebase init error:', error);
  throw error;
}
devLog('[Firebase] App initialized:', app.name);

// Initialize services
console.log('[iOS-DEBUG] 🔐 Getting auth instance...');
console.log('[iOS-DEBUG] Platform:', Capacitor.getPlatform());
console.log('[iOS-DEBUG] isNativePlatform:', Capacitor.isNativePlatform());

// Use initializeAuth with explicit persistence for Capacitor
// This fixes the signInWithCredential timeout issue in WebViews
let auth;
if (Capacitor.isNativePlatform()) {
  console.log('[iOS-DEBUG] 🔐 Using indexedDBLocalPersistence for native...');
  try {
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence
    });
    console.log('[iOS-DEBUG] ✅ Auth initialized with indexedDBLocalPersistence');
  } catch (error) {
    // Auth might already be initialized if hot reloading
    console.log('[iOS-DEBUG] ⚠️ initializeAuth failed, trying getAuth:', error.message);
    auth = getAuth(app);
  }
} else {
  console.log('[iOS-DEBUG] 🔐 Using default auth for web...');
  auth = getAuth(app);
}
export { auth };
console.log('[iOS-DEBUG] ✅ Auth instance ready');

// Initialize Firestore with memory-only cache (no IndexedDB persistence)
// This prevents stale cache data from causing sync toggle issues
console.log('[iOS-DEBUG] 📁 Initializing Firestore...');
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache()
});
console.log('[iOS-DEBUG] ✅ Firestore ready');

console.log('[iOS-DEBUG] ⚡ Getting Functions instance...');
export const functions = getFunctions(app);
console.log('[iOS-DEBUG] ✅ Functions ready');

console.log('[iOS-DEBUG] 🎉 All Firebase services initialized!');

export default app;
